import type { Alert, AlertSeverity, CommandStatus, Device } from "./types";

/**
 * The four reserved status levels, plus the two states that are not a judgement:
 * a device that is off (neutral) and one staff switched off on purpose.
 */
export type Level = "good" | "warning" | "serious" | "critical" | "neutral";

export interface Health {
  level: Level;
  /** Always shown next to the colour: colour alone never carries the meaning. */
  label: string;
  /** What is wrong, most important first. */
  reasons: string[];
}

const SEVERITY_LEVEL: Record<AlertSeverity, Level> = { warning: "warning", critical: "critical" };
const LEVEL_RANK: Record<Level, number> = { neutral: 0, good: 1, warning: 2, serious: 3, critical: 4 };

export function worst(levels: Level[]): Level {
  return levels.reduce<Level>((a, b) => (LEVEL_RANK[b] > LEVEL_RANK[a] ? b : a), "neutral");
}

/** How a device is doing, from its connection and its unresolved alerts. */
export function deviceHealth(device: Device, alerts: Alert[] = []): Health {
  if (device.status === "disabled") {
    return { level: "neutral", label: "Disabled", reasons: ["Switched off by staff"] };
  }
  const active = alerts
    .filter((alert) => alert.device_id === device.id && alert.status !== "resolved")
    .sort((a, b) => LEVEL_RANK[SEVERITY_LEVEL[b.severity]] - LEVEL_RANK[SEVERITY_LEVEL[a.severity]]);
  const reasons = active.map((alert) => alert.message);
  const alertLevel = worst(active.map((alert) => SEVERITY_LEVEL[alert.severity]));

  if (!device.is_online) {
    if (!device.last_connected_at) {
      return { level: "neutral", label: "Never connected", reasons };
    }
    // Being offline is only an alarm once the server has raised the alert for it
    // (after its grace period); until then the device may just be restarting.
    return {
      level: alertLevel === "neutral" ? "neutral" : alertLevel,
      label: "Offline",
      reasons,
    };
  }
  if (alertLevel === "critical") return { level: "critical", label: "Critical", reasons };
  if (alertLevel === "warning") return { level: "warning", label: "Warning", reasons };
  return { level: "good", label: "Online", reasons };
}

export function isAlerting(health: Health): boolean {
  return health.level === "warning" || health.level === "serious" || health.level === "critical";
}

export function severityLevel(severity: AlertSeverity): Level {
  return SEVERITY_LEVEL[severity];
}

export function batteryLevel(percent: number | null | undefined, charging?: boolean | null): Level {
  if (percent === null || percent === undefined) return "neutral";
  if (charging) return "good";
  if (percent < 10) return "critical";
  if (percent < 20) return "warning";
  return "good";
}

export function temperatureLevel(celsius: number | null | undefined, limit = 45): Level {
  if (celsius === null || celsius === undefined) return "neutral";
  if (celsius >= limit) return "critical";
  if (celsius >= limit - 3) return "warning";
  return "good";
}

export function usageLevel(percent: number | null | undefined, limit = 90): Level {
  if (percent === null || percent === undefined) return "neutral";
  if (percent >= limit) return "warning";
  return "good";
}

// --- commands -----------------------------------------------------------------------------

export const COMMAND_STATUS: Record<CommandStatus, { label: string; level: Level; done: boolean }> = {
  pending: { label: "Queued", level: "neutral", done: false },
  sent: { label: "Sent", level: "neutral", done: false },
  acked: { label: "Running", level: "neutral", done: false },
  completed: { label: "Completed", level: "good", done: true },
  failed: { label: "Failed", level: "critical", done: true },
  cancelled: { label: "Cancelled", level: "neutral", done: true },
  expired: { label: "Expired", level: "warning", done: true },
};

/** The order in which statuses are shown in progress bars and legends. */
export const COMMAND_STATUS_ORDER: CommandStatus[] = [
  "completed",
  "failed",
  "expired",
  "cancelled",
  "acked",
  "sent",
  "pending",
];
